import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { Button, Callout, Skeleton } from "@/components/ui";
import { Separator } from "@/components/ui/Separator";
import type { CarePlanPreviewItem } from "@/features/Charting/CarePlanPreview/network/useUniversalClinicCarePlanPreview";
import { Fragment } from "react";

interface UniversalClinicCarePlanPreviewContentProps {
  carePlanIds: string[];
  carePlans?: CarePlanPreviewItem[];
  isLoading: boolean;
  isError: boolean;
  hasCareContent?: boolean;
  onSendCarePlan?: () => void | Promise<void>;
  isSending?: boolean;
  sendButtonLabel?: string;
  allowSendWithoutCareContent?: boolean;
}

export function UniversalClinicCarePlanPreviewContent({
  carePlanIds,
  carePlans,
  isLoading,
  isError,
  hasCareContent,
  onSendCarePlan,
  isSending = false,
  sendButtonLabel = "Send Care Plan",
  allowSendWithoutCareContent = false,
}: UniversalClinicCarePlanPreviewContentProps) {
  return (
    <>
      {isError && (
        <Callout
          className="w-full"
          variant="danger"
          title="Failed to load care plan preview!"
          description="Please refresh and try again."
        />
      )}

      {isLoading && (
        <div className="grid gap-1">
          {Array.from({ length: 8 }).map((_, index) => (
            //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
            <Skeleton className="h-8" key={index} />
          ))}
        </div>
      )}

      {!isLoading && !isError && carePlans && carePlans.length > 0 && (
        <div className="max-h-[70vh] overflow-y-auto">
          {carePlans.map((carePlan, index) => (
            <Fragment key={carePlan.careplanId}>
              {index > 0 && <Separator className="my-4" />}
              <MarkdownRenderer content={carePlan.markdown} />
              {!carePlan.hasCareContent && (
                <Callout
                  className="w-full mt-2"
                  variant="warning"
                  title="No Care Plan Items"
                  description={
                    allowSendWithoutCareContent
                      ? "This care plan has no orders, instructions, or follow-ups."
                      : "Care plan cannot be sent without at least one order, instruction, or follow-up."
                  }
                />
              )}
            </Fragment>
          ))}
        </div>
      )}

      {!isLoading && !isError && carePlanIds.length === 0 && (
        <Callout
          className="w-full"
          variant="info"
          title="No Care Plan"
          description="No care plan exists for this visit."
        />
      )}

      {!isLoading &&
        !isError &&
        carePlanIds.length > 0 &&
        carePlans?.length === 0 && (
          <Callout
            className="w-full"
            variant="info"
            title="No Care Plan Content"
            description="The care plan preview is empty."
          />
        )}

      {onSendCarePlan && !isLoading && !isError && (
        <div className="flex flex-row gap-2 justify-self-end">
          <Button
            className="w-fit ml-auto"
            size="sm"
            onClick={onSendCarePlan}
            isLoading={isSending}
            disabled={!hasCareContent && !allowSendWithoutCareContent}
          >
            {sendButtonLabel}
          </Button>
        </div>
      )}
    </>
  );
}
